import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";

import { RootState } from "../../../app/store";
import { fetchCaseInfo } from "../../../thunks/Case";
import { Stakeholder } from "../../../api/stakeholders";
import {
  BusinessDetails,
  BusinessKeyPersonnelDetails,
  CustomerDetails,
  EmployeeDetails,
  StakeholderContactDetail,
  StakeholderKycDetail,
} from "../../../api/enrichedStakeholder";
import { EntityType, RELATED_AS_LABELS, RelatedAs, StakeholderRelationship } from "../../../api/stakeholder_relationships";
import {
  fetchEnrichedStakeholder,
  createStakeholders,
  updateStakeholder,
  createStakeholderRelationship,
  updateCustomerDetails,
  updateBusinessDetails,
  updateStakeholderKycDetails,
  updateStakeholderContactDetails,
  updateBusinessKeyPersonnelDetails,
  updateEmployeeDetails,
} from "./stakeholderThunks";
import { EMPLOYEE_ROLES } from "../../../../src/api/creditAccounts";
import { addCreditAccounts } from "../../creditAccounts/logic/thunks";
import { addAccounts } from "../../accounts/logic/thunks";
import { getAccountProductInfoByAccountNumberMap } from "../../creditAccounts/logic/slice";
import { PartyInvolved } from "../../../api/PartiesInvolved";
import { isCreditAccountStakeholdersRevampEnabled } from "../../configs/logic/Slice";
import { getPartiesByIds, selectAllPartiesInvolved } from "../../partiesinvolved/logic/PartiesInvolvedSelectors";
import { convertStakeholdersToParties } from "./util";

export interface EnrichedStakeholderDetails {
  stakeholder_id: string;
  customer_details?: CustomerDetails;
  kyc_details?: StakeholderKycDetail[];
  contact_details?: StakeholderContactDetail[];
  business_key_personnel_details?: BusinessKeyPersonnelDetails;
  business_details?: BusinessDetails;
  employee_details?: EmployeeDetails;
}

export interface FullStakeholderData {
  stakeholder: Stakeholder;
  enriched_stakeholder: EnrichedStakeholderDetails;
  relationships: StakeholderRelationship[];
}

export interface StakeholdersState {
  stakeholders: EntityState<Stakeholder, string>;
  relationships: EntityState<StakeholderRelationship, string>;
  enrichedStakeholders: EntityState<EnrichedStakeholderDetails, string>;
}

export const stakeholdersAdapter = createEntityAdapter<Stakeholder, string>({
  selectId: (stakeholder) => stakeholder.stakeholder_id,
});

export const stakeholderRelationshipsAdapter = createEntityAdapter<StakeholderRelationship, string>({
  selectId: (relationship) => relationship.relationship_id,
});

export const enrichedStakeholdersAdapter = createEntityAdapter<EnrichedStakeholderDetails, string>({
  selectId: (stakeholder) => stakeholder.stakeholder_id,
});

const initialState: StakeholdersState = {
  stakeholders: stakeholdersAdapter.getInitialState(),
  relationships: stakeholderRelationshipsAdapter.getInitialState(),
  enrichedStakeholders: enrichedStakeholdersAdapter.getInitialState(),
};

const stakeholdersSlice = createSlice({
  name: "stakeholders",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCaseInfo.pending, (state) => {
        stakeholdersAdapter.removeAll(state.stakeholders);
        stakeholderRelationshipsAdapter.removeAll(state.relationships);
        enrichedStakeholdersAdapter.removeAll(state.enrichedStakeholders);
      })

      .addCase(fetchCaseInfo.fulfilled, (state, action) => {
        const caseData = action.payload.cases?.[0];
        if (!caseData) return;

        stakeholdersAdapter.setAll(state.stakeholders, caseData.stakeholders ?? []);
        stakeholderRelationshipsAdapter.setAll(state.relationships, caseData.stakeholder_relationships ?? []);
      })

      .addCase(fetchEnrichedStakeholder.fulfilled, (state, action) => {
        const enrichedStakeholders = action.payload;
        if (!enrichedStakeholders) return;

        enrichedStakeholdersAdapter.upsertMany(state.enrichedStakeholders, enrichedStakeholders.map(
          (enrichedStakeholder) => ({
            stakeholder_id: enrichedStakeholder.stakeholder.stakeholder_id,
            customer_details: enrichedStakeholder.customer_details,
            kyc_details: enrichedStakeholder.kyc_details,
            contact_details: enrichedStakeholder.contact_details,
            business_key_personnel_details: enrichedStakeholder.business_key_personnel_details,
            business_details: enrichedStakeholder.business_details,
            employee_details: enrichedStakeholder.employee_details,
          })
        ));
      })

      .addCase(createStakeholders.fulfilled, (state, action) => {
        const enrichedStakeholders = action.payload.stakeholders ?? [];
        if (!enrichedStakeholders.length) return;

        const stakeholdersToUpsert: Stakeholder[] = [];
        const enrichedToUpsert: EnrichedStakeholderDetails[] = [];
        const relationshipsToUpsert: StakeholderRelationship[] = [];

        enrichedStakeholders.forEach((enriched) => {
          if (enriched.stakeholder) {
            stakeholdersToUpsert.push(enriched.stakeholder);

            enrichedToUpsert.push({
              stakeholder_id: enriched.stakeholder.stakeholder_id,
              customer_details: enriched.customer_details,
              kyc_details: enriched.kyc_details,
              contact_details: enriched.contact_details,
              business_key_personnel_details: enriched.business_key_personnel_details,
              business_details: enriched.business_details,
              employee_details: enriched.employee_details,
            });
          }

          if (enriched.relationships?.length) {
            relationshipsToUpsert.push(...enriched.relationships);
          }
        });

        if (stakeholdersToUpsert.length) {
          stakeholdersAdapter.upsertMany(state.stakeholders, stakeholdersToUpsert);
        }
        if (enrichedToUpsert.length) {
          enrichedStakeholdersAdapter.upsertMany(state.enrichedStakeholders, enrichedToUpsert);
        }
        if (relationshipsToUpsert.length) {
          stakeholderRelationshipsAdapter.upsertMany(state.relationships, relationshipsToUpsert);
        }
      })

      .addCase(updateStakeholder.fulfilled, (state, action) => {
        const { stakeholderId, request } = action.meta.arg;
        const existingStakeholder = state.stakeholders.entities[stakeholderId];

        if (!existingStakeholder) return;

        stakeholdersAdapter.updateOne(state.stakeholders, {
          id: stakeholderId,
          changes: request,
        });
      })

      .addCase(updateCustomerDetails.fulfilled, (state, action) => {
        const { stakeholderId, request } = action.meta.arg;
        enrichedStakeholdersAdapter.updateOne(state.enrichedStakeholders, {
          id: stakeholderId,
          changes: { customer_details: request },
        });
      })

      .addCase(updateBusinessDetails.fulfilled, (state, action) => {
        const { stakeholderId, request } = action.meta.arg;
        enrichedStakeholdersAdapter.updateOne(state.enrichedStakeholders, {
          id: stakeholderId,
          changes: { business_details: request },
        });
      })

      .addCase(updateStakeholderKycDetails.fulfilled, (state, action) => {
        const { stakeholderId, request } = action.meta.arg;
        const existingEnriched = state.enrichedStakeholders.entities[stakeholderId];

        enrichedStakeholdersAdapter.upsertOne(state.enrichedStakeholders, {
          ...(existingEnriched ?? { stakeholder_id: stakeholderId }),
          stakeholder_id: stakeholderId,
          kyc_details: request.kyc_details,
        });
      })

      .addCase(updateStakeholderContactDetails.fulfilled, (state, action) => {
        const { stakeholderId, request } = action.meta.arg;
        const existingEnriched = state.enrichedStakeholders.entities[stakeholderId];
        const currentContacts = existingEnriched?.contact_details ?? [];

        const contactIndex = currentContacts.findIndex((c) => c.contact_id === request.contact_id);
        const updatedContacts =
          contactIndex >= 0
            ? currentContacts.map((c) => (c.contact_id === request.contact_id ? request : c))
            : [...currentContacts, request];

        enrichedStakeholdersAdapter.upsertOne(state.enrichedStakeholders, {
          ...(existingEnriched ?? { stakeholder_id: stakeholderId }),
          stakeholder_id: stakeholderId,
          contact_details: updatedContacts,
        });
      })

      .addCase(updateBusinessKeyPersonnelDetails.fulfilled, (state, action) => {
        const { stakeholderId, request } = action.meta.arg;
        const existingEnriched = state.enrichedStakeholders.entities[stakeholderId];

        enrichedStakeholdersAdapter.upsertOne(state.enrichedStakeholders, {
          ...(existingEnriched ?? { stakeholder_id: stakeholderId }),
          stakeholder_id: stakeholderId,
          business_key_personnel_details: request,
        });
      })

      .addCase(updateEmployeeDetails.fulfilled, (state, action) => {
        const { stakeholderId, request } = action.meta.arg;
        const existingEnriched = state.enrichedStakeholders.entities[stakeholderId];

        enrichedStakeholdersAdapter.upsertOne(state.enrichedStakeholders, {
          ...(existingEnriched ?? { stakeholder_id: stakeholderId }),
          stakeholder_id: stakeholderId,
          employee_details: request,
        });
      })

      .addCase(createStakeholderRelationship.fulfilled, (state, action) => {
        stakeholderRelationshipsAdapter.upsertOne(state.relationships, action.payload);
      })

      .addCase(addCreditAccounts.fulfilled, (state, action) => {
        const { stakeholders, stakeholder_relationships } = action.payload;

        if (stakeholders && stakeholders.length > 0) {
          stakeholdersAdapter.upsertMany(state.stakeholders, stakeholders);
        }

        if (stakeholder_relationships && stakeholder_relationships.length > 0) {
          stakeholderRelationshipsAdapter.upsertMany(state.relationships, stakeholder_relationships);
        }
      })

      .addCase(addAccounts.fulfilled, (state, action) => {
        const { stakeholders, stakeholder_relationships } = action.payload;

        if (stakeholders && stakeholders.length > 0) {
          stakeholdersAdapter.upsertMany(state.stakeholders, stakeholders);
        }

        if (stakeholder_relationships && stakeholder_relationships.length > 0) {
          stakeholderRelationshipsAdapter.upsertMany(state.relationships, stakeholder_relationships);
        }
      });
  },
});

export const stakeholdersReducer = stakeholdersSlice.reducer;

const stakeholderSelectors = stakeholdersAdapter.getSelectors<RootState>(
  (state) => state.stakeholders.stakeholders,
);

export const {
  selectAll: getStakeholders,
  selectById: getStakeholderById,
  selectEntities: getStakeholderEntities,
  selectIds: getStakeholderIds,
  selectTotal: getStakeholderCount,
} = stakeholderSelectors;

const stakeholderRelationshipSelectors = stakeholderRelationshipsAdapter.getSelectors<RootState>(
  (state) => state.stakeholders.relationships,
);

export const {
  selectAll: getStakeholderRelationships,
  selectById: getStakeholderRelationshipById,
  selectEntities: getStakeholderRelationshipEntities,
  selectIds: getStakeholderRelationshipIds,
  selectTotal: getStakeholderRelationshipCount,
} = stakeholderRelationshipSelectors;

const enrichedStakeholderSelectors = enrichedStakeholdersAdapter.getSelectors<RootState>(
  (state) => state.stakeholders.enrichedStakeholders,
);

export const {
  selectAll: getEnrichedStakeholders,
  selectById: getEnrichedStakeholderById,
  selectEntities: getEnrichedStakeholderEntities,
  selectIds: getEnrichedStakeholderIds,
  selectTotal: getEnrichedStakeholderCount,
} = enrichedStakeholderSelectors;

export const getStakeholderRelationshipsByEntity = createSelector(
  [
    getStakeholderRelationships,
    (_state: RootState, entityType: EntityType, _entityId: string, _relatedAs: RelatedAs[]) => entityType,
    (_state: RootState, _entityType: EntityType, entityId: string, _relatedAs: RelatedAs[]) => entityId,
    (_state: RootState, _entityType: EntityType, _entityId: string, relatedAs: RelatedAs[]) => relatedAs,
  ],
  (relationships, entityType, entityId, relatedAs) =>
    relationships.filter(
      (relationship) =>
        relationship.entity_type === entityType &&
        relationship.entity_id === entityId &&
        relatedAs.includes(relationship.related_as),
    ),
);

export const getUniqueStakeholdersByEntity = createSelector(
  [getStakeholderRelationshipsByEntity, getStakeholderEntities],
  (relationships, stakeholderEntities): Stakeholder[] => {
    const byId = new Map<string, Stakeholder>();

    relationships.forEach((relationship) => {
      const stakeholder = stakeholderEntities[relationship.stakeholder_id];
      if (stakeholder) byId.set(stakeholder.stakeholder_id, stakeholder);
    });

    return Array.from(byId.values());
  },
);

export const getStakeholderKycDetails = createSelector(
  [getEnrichedStakeholderById],
  (enriched): StakeholderKycDetail[] => enriched?.kyc_details ?? [],
);

export const getStakeholderBusinessDetails = createSelector(
  [getEnrichedStakeholderById],
  (enriched): BusinessDetails | undefined => enriched?.business_details,
);

export const getRelationshipsForStakeholder= createSelector(
  [
    getStakeholderRelationships,
    (_state: RootState, stakeholderId: string, _entityYype: EntityType) => stakeholderId,
    (_state: RootState, _stakeholderId: string, entityType: EntityType) => entityType,
  ],
  (relationShips, stakeholderId, entityType) => 
    relationShips.filter(
      (relationship) => 
        relationship.stakeholder_id === stakeholderId &&
        relationship.entity_type === entityType
  )
);

export const getAllRelationshipsForStakeholder= createSelector(
  [
    getStakeholderRelationships,
    (_state: RootState, stakeholderId: string) => stakeholderId,
  ],
  (relationShips, stakeholderId) => 
    relationShips.filter(
      (relationship) => 
        relationship.stakeholder_id === stakeholderId
  )
);

export const getStakeholderWithEnrichedDetails = createSelector(
  [getStakeholderById, getEnrichedStakeholderById],
  (stakeholder, enrichedStakeholder) => ({
    stakeholder,
    enrichedStakeholder,
  }),
);

export const getPartiesInvolved = createSelector(
  [getStakeholders, getStakeholderRelationships, getAccountProductInfoByAccountNumberMap],
  (stakeholders, relationships, accountByNumber) => {
    return stakeholders
      .filter((stakeholder) => stakeholder.is_potential_perpetrator)
      .map((stakeholder) => {
        const stakeholderRelationships = relationships.filter(
          (relationship) => relationship.stakeholder_id === stakeholder.stakeholder_id
        );

        const relatedAs = Array.from(new Set(stakeholderRelationships.map((relationship) => relationship.related_as)))
          .map((relatedAs) => RELATED_AS_LABELS[relatedAs] || relatedAs)
          .join(", ");
        
        const loanReferences = Array.from(
          new Set(
            stakeholderRelationships
              .filter((r) => r.entity_type === EntityType.LOAN_ACCOUNT)
              .map((r) => accountByNumber[r.entity_id]!.ext_account_number),
          ),
        ).join(", ");  

        return {
          stakeholderId: stakeholder.stakeholder_id,
          name: stakeholder.name,
          relatedAs: relatedAs || "-",
          loanReferences: loanReferences || "-",
        };
      });
  }
);

export const getPotentialPerpetrators = createSelector(
  [getStakeholders],
  (stakeholders) => {
    return stakeholders.filter((stakeholder) => stakeholder.is_potential_perpetrator)
  }
)

export const getRelationshipsForStakeholderIds= createSelector(
  [
    getStakeholderRelationships,
    (_state: RootState, stakeholderIds: string[]) => stakeholderIds
  ],
  (relationships, stakeholderIds) => {
    return stakeholderIds.reduce((accumulator, stakeholderId) => {
      accumulator[stakeholderId] = relationships.filter((relationship) => relationship.stakeholder_id === stakeholderId);
      return accumulator;
    }, {} as Record<string, StakeholderRelationship[]>)
  }
)


export const getEmployeesInvolved = createSelector(
  [getStakeholders, getStakeholderRelationships, getEnrichedStakeholderEntities],
  (stakeholders, relationships, enrichedStakeholders) => {
    return stakeholders
      .filter((stakeholder) =>
        relationships.some(
          (r) =>
            r.stakeholder_id === stakeholder.stakeholder_id &&
            EMPLOYEE_ROLES.includes(r.related_as)
        )
      )
      .map((stakeholder) => {
        const stakeholderRelationships = relationships.filter(
          (r) =>
            r.stakeholder_id === stakeholder.stakeholder_id &&
            EMPLOYEE_ROLES.includes(r.related_as)
        );

        const relatedAs = Array.from(new Set(stakeholderRelationships.map((relationship) => relationship.related_as)))
          .map((relatedAs) => RELATED_AS_LABELS[relatedAs] || relatedAs)
          .join(", ");

        const enrichedStakeholder = enrichedStakeholders[stakeholder.stakeholder_id];

        return {
          stakeholderId: stakeholder.stakeholder_id,
          name: stakeholder.name,
          relatedAs,
          employeeId: enrichedStakeholder?.employee_details?.employee_id ?? "--",
        };
      });
  }
);

export const getFullStakeholders = (state: RootState) => {
  const stakeholders = getStakeholders(state);
  const fullStakeholders = stakeholders.map((stakeholder) => {
    const fullStakeholder: FullStakeholderData = {
      stakeholder: stakeholder,
      enriched_stakeholder: getEnrichedStakeholderById(state, stakeholder.stakeholder_id),
      relationships: getAllRelationshipsForStakeholder(state, stakeholder.stakeholder_id)
    }

    return fullStakeholder;
  })

  return fullStakeholders;
}

export const getAllPartyPerpetrators = createSelector(
  [isCreditAccountStakeholdersRevampEnabled, getPotentialPerpetrators, getStakeholderRelationships, selectAllPartiesInvolved],
  (isRevampEnabled, stakeholders, relationships, parties) => {
    if(isRevampEnabled) {
      return convertStakeholdersToParties(stakeholders, relationships);
    } else {
      return parties;
    }
  }
)

export const getPartyPerpetratorsByIds = (ids: string[]) => (state: RootState) => {
  const isRevampEnabled = isCreditAccountStakeholdersRevampEnabled(state);
  if(isRevampEnabled) {
    const relationships =  getStakeholderRelationships(state);
    const stakeholders = ids.map(id => getStakeholderById(state, id));

    const parties = convertStakeholdersToParties(stakeholders, relationships);

    return parties.reduce((accumulator, party) => {
      accumulator[party.party_id] = party;
      return accumulator;
    }, {} as Record<string, PartyInvolved>);
    
  } else {
    return getPartiesByIds(ids)(state);
  }
}
